// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import {
    ArchetypeSpecificStepRunSummaryPanelProps,
    createSRRTopBar,
    StepRunSummaryContainer,
    useSRRTopBarData,
} from '.';
import { ReleaseStepSubmissionData, ReleaseStepTakeoffData } from '@/types';
import {
    getAutomationIdByType,
    getIsAppleAppStore,
    isPhasedReleasePaused,
    tryDeterminePhasedReleaseDaysToPercentageSchedule,
} from '@/helpers';
import { AutomationAutoStagedRollout, AutomationType } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectAutomationById } from '@/selectors/selectors';
import { PhasedReleaseGroupWidget } from '@/components/workflowOverview/widgets/WorkflowRunPhasedReleaseWidget';

export const TakeoffStepRunSummaryPanel: React.FC<ArchetypeSpecificStepRunSummaryPanelProps> = ({ stepRun, appId }) => {
    const { stepData, appStoreData, isSubmitted, appStoreState, hasSelectedBuild } = useSRRTopBarData(stepRun);

    const takeoffData = stepData as ReleaseStepTakeoffData;

    const phasedRelease = appStoreData && 'phasedRelease' in appStoreData ? appStoreData.phasedRelease : null;

    const automationRuns = stepRun.automationRuns;
    const autoStagedRolloutAutomationId =
        takeoffData.autoStagedRolloutAutomationId ?
            takeoffData.autoStagedRolloutAutomationId
        :   getAutomationIdByType(automationRuns, AutomationType.AutoStagedRollout);

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, appId, autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;
    const automatedStagedRolloutEnabled = autoStagedRolloutAutomation?.enabled;
    const hasPhasedReleaseState = !!phasedRelease;
    const isAppleAppStore = getIsAppleAppStore(takeoffData.appStoreType);
    const isAutomatedPhasedRollout = isAppleAppStore ? hasPhasedReleaseState : automatedStagedRolloutEnabled;

    const daysToPercent = tryDeterminePhasedReleaseDaysToPercentageSchedule(
        hasPhasedReleaseState,
        takeoffData.appStoreType,
        autoStagedRolloutAutomation,
    );

    const wasAccelerated =
        phasedRelease?.isManuallyAcceleratedPhasedRollout || phasedRelease?.isRunwayAcceleratedRollout;
    const wasHalted = isPhasedReleasePaused(phasedRelease?.state);

    return (
        <div>
            <StepRunSummaryContainer
                topBar={createSRRTopBar(
                    stepRun,
                    stepData as ReleaseStepSubmissionData,
                    appStoreData,
                    isSubmitted,
                    appStoreState,
                    hasSelectedBuild,
                )}
            >
                {phasedRelease && (
                    <PhasedReleaseGroupWidget
                        groupKey={0}
                        phasedGroupData={[
                            {
                                destinationStepConfigId: stepRun.stepId,
                                destination: stepRun.destinations[0],
                                daysToPercentSchedule: daysToPercent,
                                isAutomatedPhasedRollout: isAutomatedPhasedRollout,
                                isPaused: wasHalted,
                                isRunwayPaused: wasHalted,
                                isAccelerated: wasAccelerated,
                                isManuallyAcceleratedPhasedRollout: phasedRelease?.isManuallyAcceleratedPhasedRollout,
                                isRunwayAcceleratedRollout: phasedRelease?.isRunwayAcceleratedRollout,
                                isSuperseded: phasedRelease?.isSuperseded,
                                isCompleted: !!phasedRelease?.completedAt,
                                startDate: phasedRelease?.startDate,
                                blackoutEndsAt: phasedRelease?.blackoutEndsAt,
                                state: phasedRelease?.state,
                                phasedReleasePercent: phasedRelease?.phasedReleasePercent,
                                userFraction: phasedRelease?.userFraction,
                                dayNumber: phasedRelease?.dayNumber,
                                isOnBlackoutPeriod: phasedRelease?.isOnBlackoutPeriod,
                            },
                        ]}
                        appStoreBuildsByDestStepConfigId={{
                            [stepRun.stepId]: takeoffData.appStoreAvailableBuilds,
                        }}
                        isExpandedModule={false}
                        setExpandedModule={() => {}}
                        onlyShowAdoptionsOnExpand={false}
                        shouldHideAdoptions
                    />
                )}
            </StepRunSummaryContainer>
        </div>
    );
};
